import React from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { RegulatoryDossierResponse } from '../../services/benchmarkService';
import { NOT_COMPUTED, count, decimal } from './BenchmarkVocabulary';
import { dsVar } from '../../theme/tokens';

interface RegulatoryDossierModalProps {
    open: boolean;
    dossier: RegulatoryDossierResponse | null;
    onClose: () => void;
}

/** A labelled row that says so plainly when the run recorded nothing. */
const Entry: React.FC<{ label: string; value?: React.ReactNode; note?: string }> = ({ label, value, note }) => (
    <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mb: 0.25 }}>{label}</Typography>
        <Typography component="div" sx={{ fontSize: '0.875rem', color: value ? dsVar('fg-body') : dsVar('fg-muted'), lineHeight: 1.6 }}>
            {value || NOT_COMPUTED}
        </Typography>
        {note && <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-faint'), mt: 0.5 }}>{note}</Typography>}
    </Box>
);

/**
 * The model governance record for this run.
 *
 * Every claim here is now either read from the run or shown as not recorded. The
 * previous version stated a bootstrap method, a resample count, a confidence
 * interval, a p-value, a temporal split and a "cryptographic SHA-256
 * fingerprint" that were all fixed strings in the component — and concluded that
 * the result was "mathematically impossible to attribute to random variation",
 * which no confidence interval supports.
 */
const RegulatoryDossierModal: React.FC<RegulatoryDossierModalProps> = ({ open, dossier, onClose }) => {
    const downloadJson = () => {
        if (!dossier) return;
        const blob = new Blob([JSON.stringify(dossier, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `model-governance-record-${dossier.runId || dossier.runKey}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const spec = dossier?.specifications;
    const stats = dossier?.statisticalValidation;
    const interval = stats?.rocAucDelta95CI;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    backgroundColor: dsVar('page'),
                    border: `1px solid ${dsVar('line')}`,
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
                    color: dsVar('fg-body')
                }
            }}
        >
            <DialogTitle sx={{ p: 3, borderBottom: `1px solid ${dsVar('line')}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        width: 40, height: 40, borderRadius: '12px', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(255, 222, 89, 0.1)', border: '1px solid rgba(255, 222, 89, 0.3)',
                        color: dsVar('accent-fg')
                    }}
                >
                    <GavelOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: dsVar('fg') }}>
                        Model governance record
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                        {dossier?.governanceStandards || 'Model risk management summary'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: dsVar('fg-2') }} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {!dossier ? (
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2'), py: 4, textAlign: 'center' }}>
                        The governance record for this run could not be loaded.
                    </Typography>
                ) : (
                    <>
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: dsVar('fg-muted'), mb: 1.5, mt: 1 }}>
                            Scope of the test
                        </Typography>
                        <Entry
                            label="Which alerts were evaluated"
                            value={spec?.targetPopulationFrame}
                            note={spec?.targetPopulationFrame === 'alerted_only'
                                ? 'Bounded to alerts the monitoring system already raised. No claim is made about activity that never triggered an alert.'
                                : undefined}
                        />
                        <Entry label="Comparison mode" value={spec?.comparisonMode} />
                        <Entry
                            label="Evaluation periods"
                            value={spec?.evaluationPeriods?.length ? spec.evaluationPeriods.join(', ') : null}
                        />
                        <Entry
                            label="Dataset version"
                            value={spec?.datasetVersionId !== null && spec?.datasetVersionId !== undefined ? String(spec.datasetVersionId) : null}
                        />

                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: dsVar('fg-muted'), mb: 1.5, mt: 3 }}>
                            Statistical validation
                        </Typography>
                        <Entry
                            label="Improvement in ranking quality (ROC AUC delta)"
                            value={stats?.rocAucDelta !== null && stats?.rocAucDelta !== undefined ? decimal(stats.rocAucDelta) : null}
                        />
                        <Entry
                            label="95% confidence interval"
                            value={interval ? `${decimal(interval[0])} to ${decimal(interval[1])}` : null}
                            note={interval
                                ? (stats?.intervalExcludesZero
                                    // Stated as what the interval shows, not as proof.
                                    ? 'The interval lies entirely on one side of zero, so the difference is unlikely to be explained by sampling variation alone.'
                                    : 'The interval includes zero, so this run does not establish a difference.')
                                : 'Without an interval, the precision of the estimate cannot be stated.'}
                        />
                        <Entry
                            label="Bootstrap resamples"
                            value={stats?.bootstrapResamples !== null && stats?.bootstrapResamples !== undefined ? count(stats.bootstrapResamples) : null}
                        />

                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: dsVar('fg-muted'), mb: 1.5, mt: 3 }}>
                            Traceability
                        </Typography>
                        <Entry
                            label="Scoring configuration fingerprint"
                            value={spec?.scorerFingerprint
                                ? <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>{spec.scorerFingerprint}</Box>
                                : null}
                            note={spec?.scorerFingerprintAlgorithm
                                ? `${spec.scorerFingerprintAlgorithm} digest of the scoring configuration recorded with this run.`
                                : 'No scoring configuration was recorded with this run, so there is nothing to fingerprint.'}
                        />
                        <Entry label="Run identifier" value={dossier.runId} />
                        <Entry label="Record generated" value={new Date(dossier.generatedAt).toLocaleString()} />
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${dsVar('line')}` }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: dsVar('fg-2') }}>
                    Close
                </Button>
                <Button
                    onClick={downloadJson}
                    disabled={!dossier}
                    variant="contained"
                    startIcon={<DownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem',
                        backgroundColor: dsVar('accent'), color: dsVar('on-accent'), borderRadius: '8px',
                        '&:hover': { backgroundColor: '#F5D149' },
                        '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') }
                    }}
                >
                    Download record (JSON)
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RegulatoryDossierModal;
